// @ts-nocheck
class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input[type="search"]');
    this.resultsContainer = this.querySelector('.predictive-search__results');
    this.emptyStateContainer = this.querySelector('[data-search-empty-state]');
    this.statusElement = this.querySelector('.predictive-search__status');
    this.sectionId = this.dataset.sectionId || 'predictive-search';
    this.abortController = null;
    this.selectedIndex = -1;
    this.isDisabled = this.dataset.predictiveSearchDisabled === 'true';

    if (!this.input || !this.resultsContainer || this.isDisabled) return;

    this.onInput = this.debounce(this.handleInput.bind(this), 300);
    this.input.addEventListener('input', this.onInput);
    this.input.addEventListener('keydown', this.handleKeydown.bind(this));
    this.addEventListener('focusout', this.handleFocusOut.bind(this));
  }

  isEmptyInput() {
    return !this.input.value.trim();
  }

  hasHeaderEmptyState() {
    return this.dataset.hasEmptyState === 'true' && !!this.emptyStateContainer;
  }

  getRootUrl() {
    if (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) {
      return window.Shopify.routes.root;
    }
    return '/';
  }

  handleInput() {
    const searchTerm = this.input.value.trim();

    if (!searchTerm.length) {
      this.showEmptyState();
      return;
    }

    this.getSearchResults(searchTerm);
  }

  getSearchResults(searchTerm) {
    if (this.abortController) {
      this.abortController.abort();
    }

    this.abortController = new AbortController();
    this.setLoading(true);

    const params = new URLSearchParams({
      q: searchTerm,
      section_id: this.sectionId,
      'resources[type]': 'product,collection,article,page,query',
      'resources[limit]': '6',
      'resources[options][unavailable_products]': 'last',
    });

    const url = `${this.getRootUrl()}search/suggest?${params.toString()}`;

    fetch(url, { signal: this.abortController.signal })
      .then((response) => {
        if (!response.ok) {
          throw new Error(response.status);
        }
        return response.text();
      })
      .then((text) => {
        const parsedDocument = new DOMParser().parseFromString(
          text,
          'text/html'
        );
        const section = parsedDocument.querySelector(
          `#shopify-section-${this.sectionId}`
        );
        const markup = section ? section.innerHTML : '';

        this.resultsContainer.innerHTML = markup;
        this.selectedIndex = -1;

        if (markup.trim().length > 0) {
          this.open();
        } else {
          this.close();
        }
      })
      .catch((error) => {
        if (error.name !== 'AbortError') {
          this.close();
        }
      })
      .finally(() => {
        this.setLoading(false);
      });
  }

  handleKeydown(event) {
    const links = this.getResultLinks();

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      if (!links.length) return;
      this.selectedIndex = Math.min(this.selectedIndex + 1, links.length - 1);
      this.updateActiveOption(links);
      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      if (!links.length) return;
      this.selectedIndex = Math.max(this.selectedIndex - 1, 0);
      this.updateActiveOption(links);
      return;
    }

    if (event.key === 'Enter') {
      if (this.selectedIndex >= 0 && links[this.selectedIndex]) {
        event.preventDefault();
        links[this.selectedIndex].click();
        return;
      }

      if (this.hasHeaderEmptyState() && this.isEmptyInput()) {
        this.showEmptyState();
        return;
      }

      this.close();
    }

    if (event.key === 'Escape') {
      this.close();
    }
  }

  handleFocusOut(event) {
    if (this.hasHeaderEmptyState() && this.isEmptyInput()) {
      return;
    }

    if (!event.relatedTarget || !this.contains(event.relatedTarget)) {
      window.setTimeout(() => {
        if (!this.contains(document.activeElement)) {
          this.close();
        }
      }, 150);
    }
  }

  getActivePanel() {
    if (
      this.emptyStateContainer &&
      !this.emptyStateContainer.hasAttribute('hidden')
    ) {
      return this.emptyStateContainer;
    }

    if (this.resultsContainer && !this.resultsContainer.hidden) {
      return this.resultsContainer;
    }

    return null;
  }

  getResultLinks() {
    const panel = this.getActivePanel();
    if (!panel) return [];

    return Array.from(panel.querySelectorAll('a'));
  }

  updateActiveOption(links) {
    links.forEach((link, index) => {
      link.classList.toggle('is-active', index === this.selectedIndex);
    });

    if (links[this.selectedIndex]) {
      links[this.selectedIndex].scrollIntoView({
        block: 'nearest',
        behavior: 'smooth',
      });
    }
  }

  setLoading(isLoading) {
    this.classList.toggle('is-loading', isLoading);
    if (this.statusElement) {
      const loadingText = this.dataset.loadingText || 'Loading results';
      this.statusElement.textContent = isLoading ? loadingText : '';
    }

    if (isLoading && this.resultsContainer.hidden) {
      this.hideEmptyState();
      this.resultsContainer.innerHTML = this.getLoadingSkeleton();
      this.resultsContainer.hidden = false;
      this.input.setAttribute('aria-expanded', 'true');
    }
  }

  /**
   * @returns {string}
   */
  getLoadingSkeleton() {
    return `
      <div class="predictive-search-results" style="animation: fadeInDown 0.2s cubic-bezier(0.4, 0, 0.2, 1);">
        <div class="predictive-search-results__group">
          <div class="predictive-search-results__list">
            ${Array.from(
              { length: 3 },
              (_, i) => `
              <div class="predictive-search-results__item" style="animation-delay: ${i * 0.05}s;">
                <div class="predictive-search-results__product" style="pointer-events: none;">
                  <div class="predictive-search-results__product-image" style="background: rgba(0, 0, 0, 0.06); animation: pulse 1.5s cubic-bezier(0.4, 0, 0.6, 1) infinite;"></div>
                  <div class="predictive-search-results__product-info" style="width: 100%;">
                    <div style="height: 1.2rem; width: 70%; background: rgba(0, 0, 0, 0.06); border-radius: 0.25rem; animation: pulse 1.5s cubic-bezier(0.4, 0, 0.6, 1) infinite; animation-delay: 0.1s;"></div>
                    <div style="height: 1rem; width: 40%; background: rgba(0, 0, 0, 0.06); border-radius: 0.25rem; animation: pulse 1.5s cubic-bezier(0.4, 0, 0.6, 1) infinite; animation-delay: 0.2s;"></div>
                  </div>
                </div>
              </div>
            `
            ).join('')}
          </div>
        </div>
      </div>
    `;
  }

  hideEmptyState() {
    if (!this.emptyStateContainer) return;
    this.emptyStateContainer.setAttribute('hidden', '');
  }

  showEmptyState() {
    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }

    this.setLoading(false);
    this.resultsContainer.hidden = true;
    this.resultsContainer.innerHTML = '';
    this.selectedIndex = -1;

    if (!this.emptyStateContainer) {
      this.input.setAttribute('aria-expanded', 'false');
      return;
    }

    this.emptyStateContainer.removeAttribute('hidden');
    this.revealEmptyStateProductCards();
    this.input.setAttribute('aria-expanded', 'true');
  }

  revealEmptyStateProductCards() {
    if (!this.emptyStateContainer) return;

    this.emptyStateContainer
      .querySelectorAll('.product-card:not(.animate-in)')
      .forEach((card) => {
        card.classList.add('animate-in');
        card.setAttribute('data-pc-animated', 'true');
      });
  }

  open() {
    this.hideEmptyState();
    this.resultsContainer.hidden = false;
    this.input.setAttribute('aria-expanded', 'true');
  }

  close() {
    this.resultsContainer.hidden = true;
    this.resultsContainer.innerHTML = '';
    this.selectedIndex = -1;

    if (this.hasHeaderEmptyState() && this.isEmptyInput()) {
      this.showEmptyState();
      return;
    }

    this.hideEmptyState();
    this.input.setAttribute('aria-expanded', 'false');
  }

  debounce(fn, wait) {
    let timeoutId;
    return (...args) => {
      window.clearTimeout(timeoutId);
      timeoutId = window.setTimeout(() => fn.apply(this, args), wait);
    };
  }
}

if (!customElements.get('predictive-search')) {
  customElements.define('predictive-search', PredictiveSearch);
}

document.addEventListener('dialog:open', (event) => {
  const host = event.target;
  if (
    !(host instanceof HTMLElement) ||
    !host.id.startsWith('header-search-dialog-')
  ) {
    return;
  }

  const liveDialog = document.querySelector(
    `dialog.theme-dialog[data-dialog-id="${host.id}"]`
  );
  const input = liveDialog?.querySelector('input[type="search"]');
  if (!(input instanceof HTMLInputElement)) {
    return;
  }

  const predictiveSearch = liveDialog?.querySelector('predictive-search');

  window.setTimeout(() => {
    input.focus();
    input.select();

    if (predictiveSearch instanceof PredictiveSearch && !input.value.trim()) {
      predictiveSearch.showEmptyState();
    }
  }, 150);
});
